import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { authAxios, apiErrorMessage } from '@/lib/api';
import { useApiData } from '@/hooks/useApiData';
import LandingPageEditor from './landing/LandingPageEditor';
import LandingPageRow from './landing/LandingPageRow';

const NEW_KEY = '__new__';
const NO_ITEMS = [];
const EMPTY_FORM = { slug: '', country: '', flag_emoji: '', meta_title: '', meta_description: '', keywords: '', h1: '', intro: '', content: '', products: [], cta_text: 'Request a Quote', published: true };

export default function LandingPagesManager() {
  const { data: pages, reload } = useApiData('/api/landing-pages?published_only=false', NO_ITEMS, authAxios);
  const { data: products } = useApiData('/api/products', NO_ITEMS);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const startNew = () => { setEditing(NEW_KEY); setForm(EMPTY_FORM); };
  const startEdit = (page) => { setEditing(page.slug); setForm({ ...EMPTY_FORM, ...page }); };
  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };

  const savePage = async () => {
    setSaving(true);
    try {
      if (editing === NEW_KEY) await authAxios.post('/api/landing-pages', form);
      else await authAxios.put(`/api/landing-pages/${editing}`, form);
      cancel();
      reload();
    } catch (e) {
      alert(apiErrorMessage(e) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (slug) => {
    if (!window.confirm(`Delete landing page /${slug}?`)) return;
    try {
      await authAxios.delete(`/api/landing-pages/${slug}`);
      reload();
    } catch (e) {
      alert(apiErrorMessage(e) || 'Delete failed');
    }
  };

  if (editing) {
    const isNew = editing === NEW_KEY;
    return (
      <LandingPageEditor
        form={form} setForm={setForm} isNew={isNew} title={isNew ? 'New Landing Page' : `Edit: /${editing}`}
        products={products} saving={saving} onSave={savePage} onCancel={cancel}
      />
    );
  }

  return (
    <div data-testid="landing-pages-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-earth/40 font-sans" data-testid="landing-pages-count">{pages.length} country/keyword landing pages for SEO</p>
        <button onClick={startNew} className="bg-saffron text-white font-sans font-semibold text-xs px-4 py-2 rounded-md hover:bg-saffron-dark flex items-center gap-1" data-testid="new-landing">
          <Plus size={12} /> New Landing Page
        </button>
      </div>
      <div className="space-y-2">
        {pages.map(page => <LandingPageRow key={page.slug} page={page} onEdit={startEdit} onDelete={deletePage} />)}
      </div>
    </div>
  );
}
